
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";
import { Profile, UserMarket } from "@/types/auth";

export const authService = {
  // Fetch user profile
  fetchProfile: async (userId: string): Promise<Profile | null> => {
    try {
      logger.debug("Fetching profile for user");
      const { data, error } = await supabase
        .from("profiles")
        .select("id, full_name, business_name, address, city, state, zip_code, phone_number, email, profile_image_url, sms_opt_in, hide_gross_margin, profile_slug")
        .eq("id", userId)
        .maybeSingle(); // Use maybeSingle to avoid error when no profile exists

      if (error) {
        logger.error("Error fetching profile", { error: error.message });
        return null;
      }
      
      if (!data) {
        logger.debug("No profile found for user (likely a buyer)");
        return null;
      }
      
      logger.debug("Profile fetched successfully");
      return data as unknown as Profile;
    } catch (error) {
      logger.error("Error fetching profile", { error });
      return null;
    }
  },

  // Fetch user markets
  fetchMarkets: async (userId: string): Promise<UserMarket[]> => {
    try {
      logger.debug("Fetching markets for user");
      const { data, error } = await supabase
        .from("user_markets")
        .select("*")
        .eq("user_id", userId);

      if (error) {
        logger.error("Error fetching markets", { error: error.message });
        return [];
      }
      
      logger.debug("Markets fetched successfully");
      return data || [];
    } catch (error) {
      logger.error("Error fetching markets", { error });
      return [];
    }
  },

  // Update user profile
  updateProfile: async (userId: string, updates: Partial<Profile>): Promise<void> => {
    const { error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", userId);

    if (error) {
      throw error;
    }
  },

  // Update user markets
  updateMarkets: async (userId: string, stateIds: string[]): Promise<void> => {
    // Delete existing markets
    const { error: deleteError } = await supabase
      .from("user_markets")
      .delete()
      .eq("user_id", userId);

    if (deleteError) {
      throw deleteError;
    }

    // Insert new markets
    if (stateIds.length > 0) {
      const marketRows = stateIds.map(stateId => ({
        user_id: userId,
        state_id: stateId
      }));
      
      const { error: insertError } = await supabase
        .from("user_markets")
        .insert(marketRows);

      if (insertError) {
        throw insertError;
      }
    }
  },

  // Sign up with email and password
  signUp: async (
    email: string, 
    password: string, 
    userData: Partial<Profile>,
    stateIds: string[]
  ) => {
    // Create auth user
    const { data, error } = await supabase.auth.signUp({ 
      email, 
      password,
      options: {
        data: {
          full_name: userData.full_name,
        }
      }
    });
    
    if (error) {
      throw error;
    }

    if (data.user) {
      // Update profile with additional information
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          business_name: userData.business_name,
          address: userData.address,
          city: userData.city,
          state: userData.state,
          zip_code: userData.zip_code,
          phone_number: userData.phone_number,
          email: userData.email
        })
        .eq("id", data.user.id);

      if (profileError) {
        logger.error("Error updating profile", { error: profileError.message });
      }

      // Insert markets
      if (stateIds.length > 0) {
        const marketRows = stateIds.map(stateId => ({
          user_id: data.user.id,
          state_id: stateId
        }));
        
        const { error: marketsError } = await supabase
          .from("user_markets")
          .insert(marketRows);

        if (marketsError) {
          logger.error("Error inserting markets", { error: marketsError.message });
        }
      }

      // Send welcome email (don't block registration if this fails)
      try {
        logger.debug("Sending seller welcome email");
        await supabase.functions.invoke('send-welcome-email', {
          body: {
            userType: 'seller',
            email: userData.email || email,
            fullName: userData.full_name || '',
            businessName: userData.business_name || '',
            markets: stateIds
          }
        });
        logger.debug("Seller welcome email sent successfully");
      } catch (emailError) {
        logger.error("Failed to send welcome email (non-blocking)", { error: emailError });
      }
    }

    return { data, error };
  }
};
